import React, { useEffect, useState } from "react";
import {
	MapContainer,
	TileLayer,
	useMap,
	CircleMarker,
	Popup,
	ZoomControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import HeatmapOverlay from "leaflet-heatmap";
import trans, { isRTL } from "../../../utils";
import { useGetPOIHeatMapQuery } from "../../../Redux/service/POIS/POIsHeatmap";
import HeatMapPopupHead from "./HeatmapPopup/HeatMapPopupHead";
import HeatMapPopupBody from "./HeatmapPopup/HeatMapPopupBody";
import MarkerClusterGroup from "react-leaflet-cluster";
import LeafletGeocoder from "../../Maps/MapGeoCoder";
import { toast } from "react-toastify";
import { IoCompassOutline } from "react-icons/io5";
import L from "leaflet";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
// Example data
const testData = {
	max: 100,
	data: [
		{ lat: 40.7128, lng: -74.006, count: 7 },
		{ lat: 40.7128, lng: -74.006, count: 10 },
		{ lat: 40.7128, lng: -74.006, count: 45 },
		{ lat: 40.7128, lng: -74.006, count: 10 },
		{ lat: 40.7128, lng: -74.006, count: 45 },
		{ lat: 40.7128, lng: -74.006, count: 100 },
		{ lat: 40.7128, lng: -74.006, count: 3 },
		{ lat: -90, lng: -120.8884, count: 3 },
		{ lat: -90, lng: -180, count: 3 },
		{ lat: -90, lng: -170.7377, count: 3 },
		{ lat: -90, lng: -180, count: 3 },
		{ lat: -90, lng: -170.7377, count: 10 },
		{ lat: -90, lng: -170.7377, count: 3 },
		{ lat: -90, lng: -180, count: 3 },
		{ lat: 40.71281, lng: -74.00603, count: 6 },
		{ lat: 22.20724, lng: 40.83103, count: 3 },
		{ lat: 30.76969, lng: 31.4256, count: 6 },
		{ lat: 27.97451, lng: 28.92246, count: 3 },
		{ lat: -90, lng: -180, count: 3 },
		{ lat: 89, lng: 8, count: 3 },
		{ lat: 20.0129, lng: 41.4677, count: 10 },
		{ lat: 21.62654, lng: 40.93866, count: 3 },
		{ lat: 22.11592, lng: 42.95946, count: 10 },
		{ lat: 23.7147, lng: 32.8953, count: 3 },
		{ lat: 40.7128, lng: -74.006, count: 3 },
		{ lat: 31.31667, lng: 30.06667, count: 3 },
		{ lat: 55.7128, lng: 74.006, count: 6 },
		{ lat: 25.37736, lng: 49.72154, count: 3 },
		{ lat: 30.13091, lng: 31.31941, count: 6 },
		{ lat: 30.71281, lng: 55.00603, count: 3 },
		{ lat: 31.31667, lng: 30.06667, count: 3 },
		{ lat: 30.053, lng: 31.23, count: 13 },
		{ lat: 31.0959, lng: 29.7604, count: 6 },
		{ lat: 31.11174, lng: 30.93991, count: 3 },
		{ lat: 31.41648, lng: 31.81332, count: 16 },
		{ lat: 26.22992, lng: 20.60547, count: 23 },
		{ lat: 55.7128, lng: 74.006, count: 100006 },
		{ lat: 55.7128, lng: 74.006, count: 6 },
		{ lat: 55.7128, lng: 74.006, count: 3 },
		{ lat: 55.7128, lng: 74.006, count: 3 },
		{ lat: 55.7128, lng: 74.006, count: 6 },
		{ lat: 55.7128, lng: 74.006, count: 10 },
		{ lat: 55.7128, lng: 74.006, count: 3 },
		{ lat: 55.7128, lng: 74.006, count: 3 },
		{ lat: 55.7128, lng: 74.006, count: 3 },
		{ lat: 55.7128, lng: 74.006, count: 13 },
		{ lat: 55.7128, lng: 74.006, count: 6 },
		{ lat: 30.06866, lng: 31.24747, count: 3 },
		{ lat: 31.31667, lng: 30.06667, count: 10 },
	],
};

// Base heatmap config
const heatmapConfig = {
	radius: 20,
	maxOpacity: 0.8,
	scaleRadius: false,
	useLocalExtrema: true,
	latField: "lat",
	lngField: "lng",
	valueField: "taskCount",
};

// Function to scale radius by zoom level
const radiusScale = (count, maxCount, zoom) => {
	const minR = 10; // at max zoom, show nearly as dots
	const maxR = 40; // at min zoom, blobs are larger

	const countFactor = count / maxCount;
	const baseRadius = minR + countFactor * (maxR - minR);

	// Inverse relation: more zoom → smaller radius
	const zoomFactor = Math.max(1, (18 - zoom) / 2);
	return baseRadius * zoomFactor;
};

function HeatmapLayer({ data }) {
	const map = useMap();

	useEffect(() => {
		const heatmapLayer = new HeatmapOverlay(heatmapConfig);
		map.addLayer(heatmapLayer);

		const updateHeatmap = () => {
			const zoom = map.getZoom();
			const processed = {
				max: data.max,
				data: data.data.map((p) => ({
					...p,
					radius: radiusScale(p.count, data.max, zoom),
				})),
			};
			heatmapLayer.setData(processed);
		};

		// Initial + update on zoom
		updateHeatmap();
		map.on("zoomend", updateHeatmap);

		return () => {
			map.removeLayer(heatmapLayer);
			map.off("zoomend", updateHeatmap);
		};
	}, [map, data]);

	return null;
}

function ClickablePoints({ data, formattedData }) {
	console.log("FormattedData", formattedData);
	return (
		<>
			{formattedData?.data?.map((uniquePoint) => {
				// collect all points that match this lat/lng
				const matches = data.filter(
					(p) => p.lat === uniquePoint.lat && p.lng === uniquePoint.lng
				);
				console.log("Matches", matches);
				return (
					<CircleMarker
						key={`${uniquePoint.lat}-${uniquePoint.lng}`}
						center={[uniquePoint.lat, uniquePoint.lng]}
						radius={5}
						pathOptions={{ color: "transparent", fillOpacity: 0 }}
					>
						<Popup closeOnEscapeKey closeButton={false} autoPan={true}>
							<div
								className={`device-popup ${
									!isRTL ? "device-popup-ltr" : "device-popup-rtl"
								}`}
							>
								<HeatMapPopupHead total={matches?.length} />
								<HeatMapPopupBody data={matches} />
							</div>
						</Popup>
					</CircleMarker>
				);
			})}
		</>
	);
}
function HeatmapLegend() {
	const levels = [
		{ color: "#b4bbec", label: "low" },
		{ color: "#77fa6c", label: "moderate" },
		{ color: "#e4cc1c", label: "medium-high" },
		{ color: "#f1312b", label: "high" },
	];

	return (
		<div
			style={{
				position: "absolute",
				bottom: "20px",
				left: isRTL ? "91%" : "1%",
				padding: "8px",
				background: "white",
				border: "1px solid #ccc",
				borderRadius: "4px",
				fontSize: "12px",
				lineHeight: "16px",
				zIndex: 500,
			}}
		>
			<div
				style={{
					marginBottom: "4px",
					fontWeight: "bold",
					textTransform: "capitalize",
				}}
			>
				{trans("intensity")}
			</div>
			{levels.map((level, i) => (
				<div
					key={i}
					style={{
						display: "flex",
						alignItems: "center",
						textTransform: "capitalize",
					}}
				>
					<div
						style={{
							width: "20px",
							height: "10px",
							background: level.color,
							marginInline: "5px",
						}}
					></div>
					{trans(level.label)}
				</div>
			))}
		</div>
	);
}

function ZoomLevelIndicator({ level, setLevel }) {
	const map = useMap();

	useEffect(() => {
		const updateZoomLevel = () => {
			const z = map.getZoom();
			if (z <= 6) setLevel("Country");
			else if (z <= 9) setLevel("State");
			else setLevel("City");
		};

		updateZoomLevel(); // initial
		map.on("zoomend", updateZoomLevel);
		return () => map.off("zoomend", updateZoomLevel);
	}, [map]);

	return (
		<div
			style={{
				position: "absolute",
				top: "1%",
				left: isRTL ? "92%" : "1%",
				padding: "6px 10px",
				background: "white",
				border: "1px solid #D2D5DC",
				borderRadius: "4px",
				fontSize: "13px",
				zIndex: "500",
				display: "flex",
				alignItems: "center",
			}}
		>
			<div
				className={!isRTL ? "me-2" : "ms-2"}
				style={{ color: "#42526E", fontWeight: "400", fontSize: "14px" }}
			>
				{trans("Level")}:
			</div>
			<div style={{ color: "#2E9245", fontWeight: "600" }}>{trans(level)}</div>
		</div>
	);
}

const getHeatmapColor = (value, maxValue) => {
	console.log(
		"taskCount:",
		value,
		"max:",
		maxValue,
		"ratio:",
		value / maxValue
	);

	if (value === 0) return "#b4bbec"; // Light blue for no tasks

	const intensity = (value / maxValue).toFixed(3);
	if (intensity < 0.33) return "#77fa6c"; // Green for low intensity
	if (intensity < 0.66) return "#e4cc1c"; // Yellow for medium intensity
	return "#f1312b"; // Red for high intensity
};
function GetLocation() {
	const map = useMap();
	const [loading, setLoading] = useState(false);

	L.Icon.Default.mergeOptions({
		iconUrl: "https://www.svgrepo.com/show/513317/location-pin.svg",
	});

	const getLocation = () => {
		if (navigator.geolocation) {
			setLoading(true);
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords;
					map.flyTo([latitude, longitude], 18, {
						animate: true,
						duration: 1, // in seconds
					});
					L.marker([latitude, longitude]).addTo(map);
					setLoading(false);
				},
				(error) => {
					toast.error(trans("Error: Unable to retrieve location"), {
						autoClose: 800,
						rtl: localStorage.getItem("lang") === "ar" ? true : false,
					});
					setLoading(false);
				}
			);
		} else {
			toast.info(trans("Geolocation not supported"), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		}
	};
	return (
		<div>
			{loading ? (
				<Spin
					indicator={<LoadingOutlined color="#0F6936" spin />}
					size="medium"
					style={{
						color: "#0F6936",
						position: "absolute",
						top: "10%",
						left: !isRTL ? "98%" : "2%",
						zIndex: "500",
						background: "white",
						borderRadius: "4px",
						width: "30px",
						height: "30px",
						padding: "4px",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						transform: "translateX(-50%)",
					}}
				/>
			) : (
				<IoCompassOutline
					style={{
						position: "absolute",
						top: "10%",
						left: !isRTL ? "98%" : "2%",
						zIndex: "500",
						background: "white",
						borderRadius: "4px",
						transform: "translateX(-50%)",
						width: "30px",
						height: "30px",
						cursor: "pointer",
					}}
					onClick={getLocation}
				/>
			)}
		</div>
	);
}
export default function HeatmapMap({ startDate, endDate }) {
	const { data: POIHeatData } = useGetPOIHeatMapQuery(
		{ fromDate: startDate, toDate: endDate },
		{ skip: !startDate || !endDate }
	);
	const [level, setLevel] = useState("");

	const [heatMapData, setHeatMapData] = useState({ max: 0, data: [] });
	useEffect(() => {
		if (POIHeatData?.status === "success") {
			const result = Object.values(
				POIHeatData?.data?.data?.reduce((acc, { lat, lng, taskCount }) => {
					const key = `${lat}-${lng}`; // unique key per (a,b)
					if (!acc[key]) {
						acc[key] = { lat, lng, taskCount: 0 };
					}
					acc[key].taskCount += taskCount;
					return acc;
				}, {})
			);
			setHeatMapData({
				max: POIHeatData?.data?.maxTaskCount,
				data: result,
				color: getHeatmapColor(result, POIHeatData?.data?.maxTaskCount),
			});
		}
	}, [POIHeatData]);
	console.log("Map Data", heatMapData);

	return (
		<div style={{ width: "100%" }}>
			<MapContainer
				center={[23.8859, 45.0792]}
				zoom={4}
				minZoom={4}
				maxZoom={18}
				zoomControl={false}
				scrollWheelZoom={true}
				dragging={true}
				style={{
					height: "calc(100vh - 192px)",
					width: "100%",
				}}
			>
				<ZoomControl position={isRTL ? "bottomleft" : "bottomright"} />
				<LeafletGeocoder />
				<GetLocation />
				<TileLayer url="https://mt0.google.com/vt/lyrs=m,&hl=en&x={x}&y={y}&z={z}&s=Ga" />
				{/*<MarkerClusterGroup chunkedLoading>
					{heatMapData.data.map((point, idx) => (
						<CircleMarker
							key={idx}
							center={[point.lat, point.lng]}
							radius={8} // fixed radius, can scale with zoom or taskCount
							fillColor={getHeatmapColor(point.taskCount, heatMapData.max)}
							color={getHeatmapColor(point.taskCount, heatMapData.max)}
							fillOpacity={0.9}
						/>
					))}
				</MarkerClusterGroup>*/}
				<HeatmapLegend />
				<HeatmapLayer data={heatMapData} />
				<ZoomLevelIndicator level={level} setLevel={setLevel} />
				{level === "City" && (
					<ClickablePoints
						data={POIHeatData?.data?.data}
						formattedData={heatMapData}
					/>
				)}
			</MapContainer>
		</div>
	);
}
